import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import QueryString from 'query-string';
import Swal from 'sweetalert2';

import { ApplicationState } from '../../../store';
import * as ApproveRejectWorkflowStore from '../../../store/workflow/ApproveRejectWorkflow';
import * as SearchWorkflowInboxStore from '../../../store/workflow/SearchWorkflowInbox';
import * as Models from '../../../models/reference/Reference';
import * as ModelSearchWorkflowInbox from '../../../models/workflow/SearchWorkflowInbox';
import * as ModelsWorkflowInbox from '../../../models/workflow/WorkflowInbox';
import {
    onClickFileShowIframe,
    onClickFileShow,
    parseJwt
} from '../../../modules/Common';
import { getCurrentTimestamp, safeParseJson } from 'helpers/utils';

export function isDMSFile(obj: any): obj is ModelSearchWorkflowInbox.File {
    return obj && 'file_name' in obj && 'file_path' in obj;
}

export type ViewerProps = {
    isOnlineForm?: boolean;
    handleChangeApproveReject: (mode: string) => void;
    workflowValue: ModelsWorkflowInbox.WorkflowValue;
    showmodal: boolean;
    blindopenModal: (value: boolean | string) => void;
    refreshPageChild: () => void;
    jsonReportCallback: (json: any) => void;
};

function WorkflowInboxInformationViewerViewModel(props: ViewerProps) {
    const {
        handleChangeApproveReject,
        workflowValue,
        showmodal,
        blindopenModal,
        refreshPageChild,
        jsonReportCallback
    } = props;

    const dispatch = useDispatch();
    const location = useLocation();

    const searchworkflowInbox = useSelector(
        (state: ApplicationState) => state.searchworkflowInbox!
    );
    const approveRejectWorkflow = useSelector(
        (state: ApplicationState) => state.approveRejectWorkflow!
    );

    const {
        isLoadWorkflowInboxValue,
        statusWorkflowInboxValue,
        responseWorkflowInboxValue
    } = searchworkflowInbox;

    const {
        isLoadApproveReject,
        statusResponseApproveReject,
        responseApproveReject,
        messageResponseApproveReject
    } = approveRejectWorkflow;

    // Auth
    const [userId, setUserId] = useState('');
    const [srcHistory] = useState(window.document.location.href);
    const [srcLogin, setSrcLogin] = useState('');
    const [loginModal, setLoginModal] = useState(false);
    const [controlRedirect, setControlRedirect] = useState(false);
    const [checkurl, setCheckurl] = useState('');
    const [showModalUrl, setShowModalUrl] = useState(false);
    const locationSearch = location.search;

    // Token tracked via ref so async callbacks always read the latest value
    const tokenRef = useRef('');

    // Workflow value
    const [workflowValueUse, setWorkflowValueUse] =
        useState<ModelsWorkflowInbox.WorkflowValue>({
            wfId: '',
            docNo: '',
            wfInstanceNo: '',
            userId: '',
            token: '',
            sid: '',
            appId: ''
        });

    // Data
    const [formData, setFormData] = useState<any>([]);
    const [checkresponse, setCheckresponse] = useState(true);

    // Approve / Reject
    const [messageModal, setMessageModal] = useState(false);
    const [message, setMessage] = useState('');
    const [modeApproveReject, setModeApproveReject] = useState('');
    const [docNoList, setDocNoList] = useState<any[]>([]);
    const [successModal, setSuccessModal] = useState(false);
    const [successModalHeader, setSuccessModalHeader] = useState('');

    // File viewer
    const [src, setSrc] = useState<string | undefined>(undefined);
    const [fileName, setFileName] = useState<string | undefined>(undefined);
    const [fileViewerMode, setFileViewerMode] = useState<string | undefined>(
        undefined
    );

    // Display
    const [displayApproveInChargeMode, setDisplayApproveInChargeMode] =
        useState<'step' | 'table'>('table');

    // One-time flags via refs to prevent double-execution in effects
    const hasFetchedRef = useRef(false);
    const checkresponseRef = useRef(true);
    const hasProcessedApproveRef = useRef(true);

    // ── Mount: read auth token and resolve workflowValueUse ──────────────────
    useEffect(() => {
        const ls = localStorage.getItem('WF_APPLICATION');
        if (ls) {
            const jls = JSON.parse(ls);
            const access_token = parseJwt(jls.access_token);
            const extra = JSON.parse(access_token.extra);
            if (extra.citizen_id) {
                setUserId(extra.citizen_id);
                tokenRef.current = jls.access_token;
            } else if (extra.username) {
                setUserId(extra.username);
                tokenRef.current = jls.access_token;
            }
        } else {
            setUserId('unknown');
        }

        const query = QueryString.parse(location.search) as Record<
            string,
            string
        >;
        const hasRouteParams =
            !workflowValue.wfId && !!(query.wfId || query.ref_id);

        if (hasRouteParams) {
            const {
                wfId,
                docNo,
                wfInstanceNo,
                userId: qUserId,
                token: qToken,
                ref_id,
                sid,
                app_id
            } = query;

            if (ref_id) {
                console.log("WorkflowInboxInformationViewerFn.viewmodel ", ref_id);

                //const lsCheck = localStorage.getItem('WF_APPLICATION');
                //if (!lsCheck) {
                //    window.location.href = '/auth/signin';
                //    return;
                //}
                //const atCheck = parseJwt(JSON.parse(lsCheck).access_token);
                //if (new Date(atCheck.exp * 1000) < new Date()) {
                //    window.location.href = '/auth/signin';
                //    return;
                //}

                fetch('/workflow/reference?id=' + ref_id, { method: 'GET' })
                    .then(response => {
                        if (response.status !== 200) throw new Error();
                        return response.json() as Promise<Models.Response>;
                    })
                    .then(data => {
                        const parameterDecoded = window.atob(data.parameter);
                        const params = new URLSearchParams(parameterDecoded);
                        const refWfId = params.get('wfId');
                        const refDocNo = params.get('docNo');
                        const refWfInstanceNo = params.get('wfInstanceNo');
                        const refUserId = params.get('userId') ?? '';
                        const refToken = params.get('token');
                        const refSid = params.get('app_id') || data.sid;

                        let refLocalUserId = '';
                        const lsRef = localStorage.getItem('WF_APPLICATION');
                        if (lsRef) {
                            const jls = JSON.parse(lsRef);
                            const at = parseJwt(jls.access_token);
                            const extra = JSON.parse(at.extra);
                            if (extra.citizen_id) {
                                refLocalUserId = extra.citizen_id.toLowerCase();
                                setUserId(extra.citizen_id);
                                tokenRef.current = jls.access_token;
                            } else if (extra.username) {
                                refLocalUserId = extra.username.toLowerCase();
                                setUserId(extra.username);
                                tokenRef.current = jls.access_token;
                            }
                        }

                        if (refLocalUserId !== refUserId.toLowerCase()) {
                            let tokenMeta: {
                                present: boolean;
                                iss?: string;
                                exp?: number;
                                id?: string;
                                ser?: string;
                                app?: string;
                            } = { present: !!refToken };
                            if (refToken) {
                                try {
                                    const decoded = parseJwt(refToken);
                                    tokenMeta = {
                                        present: true,
                                        iss: decoded.iss,
                                        exp: decoded.exp,
                                        id: decoded.id,
                                        ser: decoded.ser,
                                        app: decoded.app
                                    };
                                } catch {
                                    // leave tokenMeta as { present: true } with no claims
                                }
                            }
                            fetch('/v1/diagnostics/ref-mismatch', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    function_name:
                                        'WorkflowInboxInformationViewerViewModel',
                                    ref_id,
                                    parameter_decoded: parameterDecoded,
                                    ref_wf_id: refWfId,
                                    ref_doc_no: refDocNo,
                                    ref_wf_instance_no: refWfInstanceNo,
                                    ref_user_id: refUserId,
                                    ref_sid: refSid,
                                    token_present: tokenMeta.present,
                                    token_iss: tokenMeta.iss,
                                    token_exp: tokenMeta.exp,
                                    token_id: tokenMeta.id,
                                    token_ser: tokenMeta.ser,
                                    token_app: tokenMeta.app,
                                    local_user_id: refLocalUserId
                                })
                            }).catch(() => {
                                // diagnostics must never affect the login-mismatch UX
                            });

                            setSrcLogin(
                                window.location.protocol +
                                    '//' +
                                    window.location.host +
                                    '/auth/signin'
                            );
                            dispatch(
                                SearchWorkflowInboxStore.actionCreators.requestsearchworkflowInbox(
                                    true,
                                    'CLOSE',
                                    refWfId ?? '',
                                    refDocNo ?? '',
                                    refUserId,
                                    refWfInstanceNo ?? '',
                                    ''
                                )
                            );
                            const loaderEl =
                                document.getElementsByClassName(
                                    'Loader__background'
                                )[0];
                            if (loaderEl)
                                loaderEl.className = 'Loader__background hide';
                            Swal.fire({
                                title: 'ไม่สำเร็จ!',
                                html: 'กรุณาทำการ กดปุ่ม ยืนยัน<br>เพื่อทำการ Login ก่อนการใช้งาน',
                                icon: 'error',
                                showCancelButton: false,
                                confirmButtonColor: '#3085d6',
                                confirmButtonText: 'ยืนยัน!',
                                allowOutsideClick: false
                            }).then(result => {
                                if (result.value) setLoginModal(true);
                            });
                        } else {
                            setWorkflowValueUse({
                                wfId: refWfId!,
                                docNo: refDocNo!,
                                wfInstanceNo: refWfInstanceNo!,
                                userId: refUserId,
                                token: refToken!,
                                sid: refSid as any
                            });
                            setCheckurl('URL');
                            setShowModalUrl(true);
                            setControlRedirect(true);
                        }
                    })
                    .catch(() => {
                        Swal.fire({
                            title: 'ไม่สำเร็จ!',
                            text: 'ไม่สามารถโหลดข้อมูลได้ กรุณาล็อคอินใหม่',
                            icon: 'error',
                            showCancelButton: false,
                            confirmButtonColor: '#3085d6',
                            confirmButtonText: 'ยืนยัน!',
                            allowOutsideClick: false
                        }).then(result => {
                            if (result.value) {
                                window.location.href = '/auth/signin';
                            }
                        });
                    });
            } else {
                setWorkflowValueUse({
                    wfId,
                    docNo,
                    wfInstanceNo,
                    userId: qUserId,
                    token: qToken,
                    sid,
                    appId: app_id
                });
                setCheckurl('URL');
                setShowModalUrl(true);
            }
        } else {
            // Modal mode — use workflowValue from props
            setWorkflowValueUse({
                wfId: workflowValue.wfId,
                docNo: workflowValue.docNo,
                wfInstanceNo: workflowValue.wfInstanceNo,
                userId: workflowValue.userId,
                token: workflowValue.token,
                sid: workflowValue.appId,
                appId: workflowValue.appId
            });
            setCheckurl('No_URL');
            setShowModalUrl(showmodal);
        }
    }, []);

    // ── Trigger initial data fetch once workflowValueUse is ready ────────────
    useEffect(() => {
        if (!workflowValueUse.userId || !showModalUrl) return;
        if (hasFetchedRef.current) return;
        hasFetchedRef.current = true;

        dispatch(
            SearchWorkflowInboxStore.actionCreators.requestsearchworkflowInbox(
                true,
                'POST',
                workflowValueUse.wfId,
                workflowValueUse.docNo,
                workflowValueUse.userId,
                workflowValueUse.wfInstanceNo,
                workflowValueUse.appId ?? ''
            )
        );
        setShowModalUrl(false);
    }, [workflowValueUse, showModalUrl]);

    // ── Handle inbox data response ────────────────────────────────────────────
    useEffect(() => {
        if (!isLoadWorkflowInboxValue) return;

        if (
            statusWorkflowInboxValue === 200 &&
            responseWorkflowInboxValue != null &&
            checkresponseRef.current
        ) {
            checkresponseRef.current = false;
            setCheckresponse(false);
            setFormData(responseWorkflowInboxValue);
            jsonReportCallback(responseWorkflowInboxValue['jsonReport']);
            const loaderEl =
                document.getElementsByClassName('Loader__background')[0];
            if (loaderEl) loaderEl.className = 'Loader__background hide';
            autoOpenFirstFile(responseWorkflowInboxValue);
        } else if (
            statusWorkflowInboxValue === 204 &&
            checkresponseRef.current
        ) {
            const loaderEl =
                document.getElementsByClassName('Loader__background')[0];
            if (loaderEl) loaderEl.className = 'Loader__background hide';
            if (checkurl === 'No_URL') blindopenModal(false);
            dispatch(
                SearchWorkflowInboxStore.actionCreators.requestsearchworkflowInbox(
                    true,
                    'CLEAR',
                    workflowValueUse.wfId,
                    workflowValueUse.docNo,
                    workflowValueUse.userId,
                    workflowValueUse.wfInstanceNo,
                    workflowValueUse.appId ?? ''
                )
            );
            setControlRedirect(true);
        }
    }, [searchworkflowInbox]);

    // ── Handle approve / reject response ─────────────────────────────────────
    useEffect(() => {
        if (!isLoadApproveReject) return;
        if (hasProcessedApproveRef.current) return;

        if (statusResponseApproveReject === 200) {
            hasProcessedApproveRef.current = true;
            checkresponseRef.current = true;
            const loaderEl =
                document.getElementsByClassName('Loader__background')[0];
            if (loaderEl) loaderEl.className = 'Loader__background hide';
            setCheckresponse(true);
            setFormData([]);
            setMessageModal(false);
            setModeApproveReject('');
            setMessage('');
            setSuccessModal(true);
        } else if (statusResponseApproveReject === 400) {
            hasProcessedApproveRef.current = true;
            checkresponseRef.current = true;
            const loaderEl =
                document.getElementsByClassName('Loader__background')[0];
            if (loaderEl) loaderEl.className = 'Loader__background hide';
            setCheckresponse(true);
            setFormData([]);
            setMessageModal(false);
            setModeApproveReject('');
            setMessage('');

            dispatch(
                SearchWorkflowInboxStore.actionCreators.requestsearchworkflowInbox(
                    true,
                    'CLEAR',
                    workflowValueUse.wfId,
                    workflowValueUse.docNo,
                    workflowValueUse.userId,
                    workflowValueUse.wfInstanceNo,
                    workflowValueUse.appId ?? ''
                )
            );
            dispatch(
                ApproveRejectWorkflowStore.actionCreators.requestApproveWorkflow(
                    true,
                    'CLEAR',
                    '',
                    '',
                    '',
                    ''
                )
            );
            dispatch(
                SearchWorkflowInboxStore.actionCreators.requestsearchworkflowInbox(
                    true,
                    'POST',
                    workflowValueUse.wfId,
                    workflowValueUse.docNo,
                    workflowValueUse.userId,
                    workflowValueUse.wfInstanceNo,
                    workflowValueUse.appId ?? ''
                )
            );

            try {
                const msg = JSON.parse(messageResponseApproveReject ?? '');
                setTimeout(() => {
                    Swal.fire({
                        title: 'ไม่สำเร็จ!',
                        text: msg.message,
                        icon: 'error',
                        confirmButtonColor: '#3085d6',
                        confirmButtonText: 'ตกลง',
                        allowOutsideClick: false
                    });
                }, 500);
            } catch {
                setTimeout(() => {
                    Swal.fire({
                        title: 'ไม่สำเร็จ!',
                        html: messageResponseApproveReject ?? '',
                        icon: 'error',
                        confirmButtonColor: '#3085d6',
                        confirmButtonText: 'ตกลง',
                        allowOutsideClick: false
                    });
                }, 500);
            }
        }
    }, [approveRejectWorkflow]);

    // ── File auto-open helper ─────────────────────────────────────────────────
    const autoOpenFirstFile = (data: any) => {
        const listDMS = safeParseJson<ModelSearchWorkflowInbox.File[]>(
            data['listDMS']
        );
        const listFileUrl = safeParseJson<ModelSearchWorkflowInbox.FileUrl[]>(
            data['listFileUrl']
        );
        const fileList = [...(listDMS ?? []), ...(listFileUrl ?? [])].filter(
            (f?: object) => f && Object.keys(f).length > 0
        );
        if (fileList.length === 0) return;

        const firstFile = fileList[0];
        const firstFileName = firstFile.file_name;
        const filePath = isDMSFile(firstFile)
            ? firstFile.file_path
            : (firstFile as ModelSearchWorkflowInbox.FileUrl).url;
        const mode = isDMSFile(firstFile) ? '' : 's3';

        if (!firstFileName || !filePath) return;

        const syntheticEvent = {
            target: { dataset: { filepath: filePath, filename: firstFileName } }
        };
        onClickFileShowIframe(syntheticEvent, mode, tokenRef.current)
            .then((iframeSrc: string | undefined) => {
                if (!iframeSrc) return;
                setSrc(iframeSrc + `&timestamp=${getCurrentTimestamp()}`);
                setFileName(firstFileName);
                setFileViewerMode(mode);
            })
            .catch(() => {});
    };

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleClickFileShowIframe = (
        e: any,
        clickedFileName: any,
        mode: any
    ) => {
        onClickFileShowIframe(e, mode, tokenRef.current).then(
            (iframeSrc: string | undefined) => {
                if (!iframeSrc) return;
                setSrc(iframeSrc + `&timestamp=${getCurrentTimestamp()}`);
                setFileName(clickedFileName);
                setFileViewerMode(mode);
            }
        );
    };

    const handleClickFileShowFile = (
        e: any,
        clickedFileName: any,
        filePath: any,
        mode: any
    ) => {
        e.target.dataset.filepath = filePath;
        e.target.dataset.filename = clickedFileName;
        e.target.dataset.mode = mode;
        onClickFileShow(e, tokenRef.current);
    };

    const handleCloseFile = () => {
        setSrc(undefined);
        setFileName(undefined);
    };

    const handleMessage = () => {
        if (messageModal) {
            setMessageModal(false);
            setModeApproveReject('');
            setMessage('');
            setDocNoList([]);
        } else {
            setMessageModal(true);
        }
    };

    const handleSuccessModal = () => {
        const loaderEl =
            document.getElementsByClassName('Loader__background')[0];
        if (loaderEl) loaderEl.className = 'Loader__background hide';
        if (successModal) {
            dispatch(
                ApproveRejectWorkflowStore.actionCreators.requestApproveWorkflow(
                    true,
                    'CLEAR',
                    '',
                    '',
                    '',
                    ''
                )
            );
            try {
                refreshPageChild();
            } catch {
                window.location.reload();
            }
            if (checkurl === 'No_URL') {
                blindopenModal('reload');
            } else {
                setControlRedirect(true);
            }
            setSuccessModal(false);
            setSuccessModalHeader('');
        } else {
            setSuccessModal(true);
        }
    };

    const handleLoginModal = () => setLoginModal(prev => !prev);

    const blindLogin = () => setLoginModal(false);

    const onchangeMessage = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(e.target.value);
    };

    const onsuccessApproveReject = () => {
        const escapedMessage = message.replace(/[']/g, "''");
        const loaderEl =
            document.getElementsByClassName('Loader__background')[0];
        if (loaderEl) loaderEl.className = 'Loader__background show';

        const updatedList = docNoList.map((item: any) => ({
            ...item,
            message: escapedMessage
        }));
        setDocNoList(updatedList);
        hasProcessedApproveRef.current = false;

        if (modeApproveReject === 'approved') {
            dispatch(
                ApproveRejectWorkflowStore.actionCreators.requestApproveWorkflowList(
                    true,
                    'PUT',
                    updatedList,
                    escapedMessage
                )
            );
        } else {
            dispatch(
                ApproveRejectWorkflowStore.actionCreators.requestRejectWorkflowList(
                    true,
                    'PUT',
                    updatedList,
                    escapedMessage
                )
            );
        }
        handleChangeApproveReject(modeApproveReject);
    };

    const handleApprove = (
        wfInstanceNo: string,
        docNo: string,
        docUserId: string,
        wfType: string,
        sid: string
    ) => {
        setDocNoList([
            {
                app_id: sid,
                user_id: docUserId,
                admin_user_id: userId,
                wf_type: wfType,
                doc_no_list: [docNo],
                instance_no_list: [wfInstanceNo]
            }
        ]);
        setSuccessModalHeader('Approved Status');
        setMessageModal(true);
        setModeApproveReject('approved');
    };

    const handleReject = (
        wfInstanceNo: string,
        docNo: string,
        docUserId: string,
        wfType: string,
        sid: string
    ) => {
        setDocNoList([
            {
                app_id: sid,
                user_id: docUserId,
                admin_user_id: userId,
                wf_type: wfType,
                doc_no_list: [docNo],
                instance_no_list: [wfInstanceNo]
            }
        ]);
        setSuccessModalHeader('Rejected Status');
        setMessageModal(true);
        setModeApproveReject('reject');
    };

    return {
        // State
        userId,
        srcHistory,
        srcLogin,
        loginModal,
        controlRedirect,
        checkurl,
        locationSearch,
        workflowValueUse,
        formData,
        checkresponse,
        messageModal,
        message,
        modeApproveReject,
        successModal,
        successModalHeader,
        src,
        fileName,
        fileViewerMode,
        displayApproveInChargeMode,
        setDisplayApproveInChargeMode,
        // Redux passthrough
        isLoadApproveReject,
        statusResponseApproveReject,
        responseApproveReject,
        // Handlers
        blindLogin,
        onchangeMessage,
        onsuccessApproveReject,
        handleApprove,
        handleReject,
        handleClickFileShowIframe,
        handleClickFileShowFile,
        handleCloseFile,
        handleMessage,
        handleSuccessModal,
        handleLoginModal
    };
}

export default WorkflowInboxInformationViewerViewModel;
